import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getDashboard } from "../../../_api/Talent";
import { Button } from "../../../_helpers/components/Button";

const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const pad = (n) => (n < 10 ? "0" + n : n);
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const StatCard = ({ title, value, iconPath, gradient }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3">
            <div className={`flex items-center justify-center w-10 h-10 bg-linear-to-br ${gradient} rounded-lg`}>
                <svg className="w-6 h-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={iconPath} />
                </svg>
            </div>
            <div>
                <p className="text-sm font-medium text-gray-500">{title}</p>
                <p className="text-2xl font-bold text-gray-900">{value}</p>
            </div>
        </div>
    </div>
);

export const Dashboard = () => {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        getDashboard()
            .then((res) => setData(res.data))
            .catch((err) => setError(err?.response?.data?.message || "Erreur de chargement"))
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <div className="min-h-full bg-gray-50 flex items-center justify-center py-20">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-app-primary mx-auto mb-4"></div>
                    <p className="text-gray-600 font-medium">Chargement du tableau de bord...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-full bg-gray-50 flex items-center justify-center">
                <div className="text-center text-red-600">{error}</div>
            </div>
        );
    }

    return (
        <div className="min-h-full bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">
                                Tableau de bord
                            </h1>
                            <p className="mt-2 text-sm text-gray-600">
                                Vue d'ensemble de votre gestion des talents
                            </p>
                        </div>
                        <Button
                            action={() => navigate("/talents/collaborateurs")}
                            name="Voir les collaborateurs"
                        />
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {/* Stats */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                    <StatCard
                        title="Effectif total"
                        value={data?.effectif_total ?? 0}
                        gradient="from-blue-500 to-blue-600"
                        iconPath="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                    <StatCard
                        title="Candidats en cours"
                        value={data?.candidats_en_cours ?? 0}
                        gradient="from-amber-500 to-amber-600"
                        iconPath="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                    />
                    <StatCard
                        title="Échéances entretiens"
                        value={data?.entretiens_a_venir ?? 0}
                        gradient="from-emerald-500 to-emerald-600"
                        iconPath="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                    <StatCard
                        title="Objectifs à suivre"
                        value={data?.objectifs_en_retard ?? 0}
                        gradient="from-rose-500 to-rose-600"
                        iconPath="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Prochains entretiens */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                    <div className="flex items-center justify-center w-10 h-10 bg-linear-to-br from-emerald-500 to-emerald-600 rounded-lg">
                                        <svg className="w-6 h-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-bold text-gray-900">Prochains entretiens</h2>
                                        <p className="text-sm text-gray-500">{data?.prochains_entretiens?.length || 0} à venir</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => navigate("/talents/entretiens")}
                                    className="text-sm text-app-primary hover:text-app-secondary font-medium"
                                >
                                    Voir tout →
                                </button>
                            </div>
                        </div>
                        <div className="p-6">
                            {data?.prochains_entretiens?.length > 0 ? (
                                <ul className="space-y-3">
                                    {data.prochains_entretiens.slice(0, 5).map((e) => (
                                        <li key={e.id} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                                            <div className="flex items-center space-x-3">
                                                <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full text-gray-600 font-medium text-sm">
                                                    {(e.user?.first_name || e.candidate?.first_name)?.[0] || ""}
                                                    {(e.user?.last_name || e.candidate?.last_name)?.[0] || ""}
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium text-gray-900">
                                                        {e.user ? `${e.user.first_name} ${e.user.last_name}` : `${e.candidate?.first_name} ${e.candidate?.last_name}`}
                                                    </p>
                                                    <p className="text-xs text-gray-500">{e.type} · {formatDate(e.date)}</p>
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <div className="text-center py-8">
                                    <svg className="mx-auto h-12 w-12 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    <h3 className="mt-4 text-sm font-medium text-gray-900">Aucun entretien à venir</h3>
                                    <p className="mt-2 text-sm text-gray-500">Planifiez vos entretiens depuis la page Entretiens.</p>
                                    <button
                                        onClick={() => navigate("/talents/entretiens")}
                                        className="mt-4 text-sm text-app-primary hover:text-app-secondary font-medium"
                                    >
                                        Créer un entretien
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Objectifs à échéance */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                    <div className="flex items-center justify-center w-10 h-10 bg-linear-to-br from-rose-500 to-rose-600 rounded-lg">
                                        <svg className="w-6 h-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-bold text-gray-900">Objectifs à échéance</h2>
                                        <p className="text-sm text-gray-500">{data?.objectifs_a_echeance?.length || 0} dans les 30 prochains jours</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => navigate("/talents/objectifs")}
                                    className="text-sm text-app-primary hover:text-app-secondary font-medium"
                                >
                                    Voir tout →
                                </button>
                            </div>
                        </div>
                        <div className="p-6">
                            {data?.objectifs_a_echeance?.length > 0 ? (
                                <ul className="space-y-3">
                                    {data.objectifs_a_echeance.slice(0, 5).map((o) => (
                                        <li key={o.id} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                                            <div
                                                className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 -m-2 p-2 rounded"
                                                onClick={() => navigate(`/talents/collaborateurs/${o.user_id}`)}
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={(e) => e.key === "Enter" && navigate(`/talents/collaborateurs/${o.user_id}`)}
                                            >
                                                <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full text-gray-600 font-medium text-sm">
                                                    {o.user?.first_name?.[0] || ""}
                                                    {o.user?.last_name?.[0] || ""}
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium text-gray-900">{o.title}</p>
                                                    <p className="text-xs text-gray-500">
                                                        {o.user?.first_name} {o.user?.last_name} · {formatDate(o.due_date)}
                                                        {o.due_date && new Date(o.due_date) < new Date() && <span className="text-rose-600 ml-1">(en retard)</span>}
                                                    </p>
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <div className="text-center py-8">
                                    <svg className="mx-auto h-12 w-12 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <h3 className="mt-4 text-sm font-medium text-gray-900">Aucun objectif à échéance</h3>
                                    <p className="mt-2 text-sm text-gray-500">Les objectifs dont la date de fin est dans les 30 prochains jours apparaîtront ici.</p>
                                    <button
                                        onClick={() => navigate("/talents/objectifs")}
                                        className="mt-4 text-sm text-app-primary hover:text-app-secondary font-medium"
                                    >
                                        Voir les objectifs
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
